/**
 * Generator Node Types
 *
 * GeneratorNode is an AI content generation node that combines:
 * - Model selection (Claude Sonnet/Opus)
 * - Prompt input with template variables
 * - Upstream context from connected BlockNodes
 * - Streaming LLM output to downstream BlockNodes
 *
 * This node bridges structured prompts with AI generation, making it
 * ideal for content creation workflows where context flows from one
 * block, through generation, to an output block.
 */

type FlowPosition = { x: number; y: number };

// ============================================================================
// CONNECTION TYPES
// ============================================================================

/**
 * Connection from an upstream BlockNode providing context.
 *
 * GeneratorNode receives content from upstream BlockNodes to use as
 * context in the AI prompt (e.g., research notes, outlines, data).
 */
export interface GeneratorConnection {
  /** Unique ID of the connected node */
  nodeId: string;

  /** Display label for the connection (shown in chip) */
  nodeLabel: string;

  /** Type of the connected node (usually 'block') */
  nodeType: string;

  /** Text content from the upstream node */
  textContent?: string;
}

// ============================================================================
// GENERATION STATUS
// ============================================================================

/**
 * Current status of the AI generation process.
 */
export type GenerationStatus =
  | 'idle'          // Ready to generate, waiting for user action
  | 'generating'    // Currently streaming response from LLM
  | 'complete'      // Generation finished successfully
  | 'error';        // Generation failed with error

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * Data structure for GeneratorNode instances.
 *
 * This is stored in the React Flow node's data property and synced
 * via Yjs for real-time collaboration.
 */
export interface GeneratorNodeData {
  // ========== Identity ==========

  /** Node title (optional, displayed above node) */
  title?: string;

  // ========== Configuration ==========

  /** Selected AI model (e.g., 'claude-sonnet-4-5-20250929') */
  model: string;

  /** User-provided prompt template */
  prompt: string;

  /** Temperature setting (0-1, lower = more deterministic) */
  temperature?: number;

  /** Maximum tokens to generate */
  maxTokens?: number;

  // ========== Connections ==========

  /** Upstream BlockNodes providing context */
  incomingConnections?: GeneratorConnection[];

  /** ID of the downstream BlockNode to write output to */
  targetBlockId?: string;

  // ========== Status & Output ==========

  /** Current generation status */
  status: GenerationStatus;

  /** Error message if status is 'error' */
  error?: string;

  /** Last generated output (for display/reference) */
  lastOutput?: string;

  // ========== Callbacks (for parent component state management) ==========

  /** Called when title changes */
  onTitleChange?: (title: string) => void;

  /** Called when model changes */
  onModelChange?: (model: string) => void;

  /** Called when prompt changes */
  onPromptChange?: (prompt: string) => void;

  /** Called when temperature changes */
  onTemperatureChange?: (temp: number) => void;

  /** Called when user triggers generation */
  onGenerate?: () => void;

  /** Called when incoming connections are reordered */
  onIncomingConnectionsChange?: (connections: GeneratorConnection[]) => void;

  // ========== Runtime-Only Props (injected by BoardCanvas) ==========

  /** Yjs document for accessing BlockNode fragments (not stored in Yjs) */
  ydoc?: any; // Y.Doc type

  /** Board ID for API calls (not stored in Yjs) */
  boardId?: string;

  /** Yjs map of all nodes (for finding downstream BlockNode) (not stored in Yjs) */
  nodesMap?: any; // Y.Map<Node> type

  // Index signature required for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for GeneratorNode.
 *
 * Use this type when working with generator nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new generator nodes
 * - In onNodesChange handlers
 */
export type GeneratorNodeType = {
  id: string;
  type: 'generator';
  data: GeneratorNodeData;
  position: FlowPosition;
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default GeneratorNodeData with sensible defaults.
 *
 * Use this when creating a new GeneratorNode to ensure all required
 * fields are initialized.
 */
export function createDefaultGeneratorData(): GeneratorNodeData {
  return {
    title: '',
    model: 'claude-sonnet-4-5-20250929',
    prompt: '',
    temperature: 0.7,
    maxTokens: 4096,
    incomingConnections: [],
    status: 'idle',
  };
}

/**
 * Build the complete prompt by combining user prompt with upstream context.
 *
 * Useful for preview and for sending to the API endpoint.
 */
export function buildCompletePrompt(data: GeneratorNodeData): string {
  const contextParts: string[] = [];

  // Add context from upstream connections
  if (data.incomingConnections && data.incomingConnections.length > 0) {
    contextParts.push('# Context from upstream nodes:\n');
    data.incomingConnections.forEach((conn) => {
      if (conn.textContent) {
        contextParts.push(`## ${conn.nodeLabel} (@{{${conn.nodeId}}}):\n${conn.textContent}\n`);
      }
    });
    contextParts.push('\n---\n\n');
  }

  // Add user prompt
  contextParts.push(data.prompt);

  return contextParts.join('');
}
